/**
 * S3's **reference layers**, adopted from the server rather than assumed.
 *
 * > *"Country boundaries: ON by default. Watersheds and partnerships: OFF, and shown only
 * > on filter."*
 *
 * `/meta/config.theme_defaults.reference_layers` carries that table
 * (`apps/meta/themes.py`): a `key`, the §D2 `unit_type` and `admin_level` it draws, whether
 * it is on by default, and whether it appears only under a filter. This component renders
 * nothing and applies it once, when the config lands.
 *
 * ## Why it is a component and not a constant
 *
 * `hierarchy.DEFAULT_VISIBLE` already said Countries ✓, Regions ✓, the rest off — which
 * happens to satisfy the client's clause. "Happens to" is the problem: the clause is the
 * client's and the table is the server's, and a default that agrees by coincidence is a
 * default nobody will remember to change when the table does. So the three rows the config
 * names are *driven* by it, and the two it does not name (Regions, Communes) keep the §F5.8
 * defaults they have always had — the client's table is about reference outlines, and it
 * would be an over-reading to let its silence switch off a row it never mentions.
 *
 * **`filter_only` needs no code.** A row that appears only under a filter is exactly what
 * `visibilityForScope` already installs: a watershed scope turns Watersheds and Partnerships
 * on and everything else off, and clearing the filter hands the reader's own rows back
 * (`applyScope`). The flag is asserted against that behaviour in the e2e suite rather than
 * re-implemented here.
 *
 * The reader's own ticks outrank this: it runs once, before anyone can have touched the
 * Boundaries panel, and the panel remains the manual override C1 built it to be.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchConfig, queryKeys } from '../../lib/api';
import { referenceRowKey, useHierarchyStore } from './hierarchy';

export function ReferenceDefaults() {
  const applied = useRef(false);
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const rows = config?.theme_defaults?.reference_layers;

  useEffect(() => {
    if (applied.current || !rows?.length) return;
    applied.current = true;
    const { setVisible } = useHierarchyStore.getState();
    for (const entry of rows) {
      const key = referenceRowKey(entry);
      // A reference layer this build has no boundary row for is skipped rather than
      // guessed at: the config is allowed to name something the map does not draw yet.
      if (key) setVisible(key, entry.default_on);
    }
  }, [rows]);

  return null;
}

export default ReferenceDefaults;
